import { useMemo, useState } from 'react';
import { FormFieldDescriptor, FormFields, FormState } from './useForm';

export interface ValidationResult {
  isValid: boolean,
  error: string
}

export interface FieldError {
  isTouched: boolean,
  isValid: boolean,
  message: string,
}

export type FormErrors<T extends string> = Record<T, FieldError>;

export interface UseValidationResult<T extends string> {
  errors: FormErrors<T>,
  validateFields: (
    fieldNames: T[],
    state: FormState<T>
  ) => void,
  isValid: boolean,
  validateAllFields: (
    state: FormState<T>
  ) => boolean,
  resetErrors: () => void,
}

export const useValidation = <T extends string>(
  fields: FormFields<T>
): UseValidationResult<keyof typeof fields> => {
  const getSingleFieldErrors = (
    fieldName: T,
    state: FormState<T>,
    isTouched = false
  ): FormErrors<T> => {
    const fieldDescriptor = fields[fieldName] as FormFieldDescriptor<T>;
    const fieldState = state[fieldName] as string;
    let validationResult: ValidationResult | undefined;

    if (fieldDescriptor.isRequired && !fieldState) {
      validationResult = {
        isValid: false,
        error: fieldDescriptor.requiredMessage ?? 'This field is required'
      };
    } else if (!!fieldState || fieldDescriptor.validateEmpty) {
      validationResult = fieldDescriptor.validate?.(fieldState, state);
    }

    return {
      [fieldName]: {
        isTouched: isTouched || !!fieldState || fieldDescriptor.validateEmpty,
        isValid: validationResult?.isValid ?? true,
        message: validationResult?.error ?? ''
      }
    } as FormErrors<T>;
  };

  const getDependentFieldErrors = (
    fieldName: T,
    state: FormState<T>,
    isTouched?: boolean
  ): FormErrors<T> => {
    const dependentFields = fields[fieldName]?.dependentFields;
    let dependentFieldsErrors: FormErrors<T> = {} as FormErrors<T>;

    if (dependentFields?.length) {
      dependentFieldsErrors = dependentFields.reduce((result, dependetFieldName) => ({
        ...result,
        ...getSingleFieldErrors(dependetFieldName, state, isTouched)
      }), {} as FormErrors<T>);
    }

    return dependentFieldsErrors;
  };

  const getFieldErrors = (
    fieldName: T,
    state: FormState<T>,
    isTouched?: boolean
  ): FormErrors<T> => ({
    ...getSingleFieldErrors(fieldName, state, isTouched),
    ...getDependentFieldErrors(fieldName, state, isTouched)
  });

  const getFieldsErrors = (
    fieldNames: T[],
    state: FormState<T>,
    isTouched?: boolean
  ): FormErrors<T> => (
    fieldNames.reduce((fieldsErrors, fieldKey) => ({
      ...fieldsErrors,
      ...getFieldErrors(fields[fieldKey].name, state, isTouched)
    }), {} as FormErrors<T>)
  );

  const getInitialErrors = (
    fields: FormFields<T>
  ): FormErrors<T> => {
    const fieldKeys = Object.keys(fields) as T[];

    const initialState: FormState<T> = fieldKeys.reduce(
      (state: FormState<T>, fieldKey: T) => {
        const { name: fieldName, initialValue } = fields[fieldKey];

        return ({
          ...state,
          [fieldName]: initialValue ?? ''
        });
      }, {} as FormState<T>
    );

    return getFieldsErrors(fieldKeys, initialState);
  };

  const [ errors, setErrors ] = useState<FormErrors<T>>(() => getInitialErrors(fields));

  const validateFields = (
    fieldNames: T[],
    state: FormState<T>
  ): void => {
    setErrors(prevErrors => ({
      ...prevErrors,
      ...getFieldsErrors(fieldNames, state, true)
    }));
  };

  const validateAllFields = (
    state: FormState<T>
  ): boolean => {
    const newErrors = getFieldsErrors(Object.keys(fields) as T[], state, true);
    setErrors(newErrors);

    return !(Object.keys(newErrors) as T[]).find(fieldKey => !newErrors[fieldKey]?.isValid);
  };

  const isValid = useMemo(() => !(
    (Object.keys(errors) as T[]).find(fieldKey => !errors[fieldKey]?.isValid)
  ), [ errors ]);

  const resetErrors = (): void => {
    setErrors(getInitialErrors(fields));
  };

  return {
    errors,
    isValid,
    validateFields,
    validateAllFields,
    resetErrors
  };
};
